import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import CommonService from '../../../services/common.service';
import { useCurrencyMaster } from '../../../hooks/useCurrencyMaster';
import { useProductDetailsMaster } from '../../../hooks/useProductDetailsMaster';
import { getAvailableRoles, getCurrentRole } from '../../../utils/roleSwitcher';
import '../../../assets/styles/invoice_list.scss';

export default function EwayGenerationList() {
  const navigate = useNavigate();
  const location = useLocation();
  const currentRole = getCurrentRole();
  const isActiveSeller = Number(currentRole?.id) === 2;
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [acceptedInvoices, setAcceptedInvoices] = useState([]);
  const [pendingPickupShipments, setPendingPickupShipments] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEWayBill, setSelectedEWayBill] = useState(null);
  const [isUpdateVehicleModalOpen, setIsUpdateVehicleModalOpen] = useState(false);
  const [vehicleUpdateForm, setVehicleUpdateForm] = useState({
    newVehicleNumber: '',
    currentLocation: '',
    reasonForChange: ''
  });
  
  // Master Data Hooks
  const { currencyOptions } = useCurrencyMaster();
  const { attributeUnitOptions } = useProductDetailsMaster();

  // Logistics flow visibility rules:
  // - Only show Logistics shipments once pickup is confirmed/completed.
  // - Do not affect non-logistics flows (those rows won't have these fields).
  const isLogisticsFlowRow = (order) => {
    return (
      order &&
      (order.handoverPickupStatus != null ||
        order.handover_pickup_status != null ||
        order.deliveryProcess != null ||
        order.delivery_process != null)
    );
  };

  const isPickupConfirmedForLogisticsRow = (order) => {
    const raw =
      order?.handoverPickupStatus ??
      order?.handover_pickup_status ??
      order?.deliveryProcess?.handoverPickupStatus ??
      order?.delivery_process?.handoverPickupStatus ??
      null;
    const s = raw != null ? String(raw).trim().toLowerCase() : '';
    return s === 'picked_up' || s === 'pickup_confirmed' || s === 'completed';
  };

  // Helper function to get unit label
  const getUnitLabel = (unitValue) => {
    if (!unitValue) return '';
    if (!attributeUnitOptions || attributeUnitOptions.length === 0) return unitValue;
    
    const unitValueNum = isNaN(unitValue) ? null : Number(unitValue);
    const foundByValue = attributeUnitOptions.find(opt => {
      if (String(opt.value) === String(unitValue)) return true;
      if (unitValueNum !== null && !isNaN(opt.value)) {
        return Number(opt.value) === unitValueNum;
      }
      return false;
    });
    
    if (foundByValue) {
      return foundByValue.label || unitValue;
    }
    
    const foundByLabel = attributeUnitOptions.find(opt => 
      opt.label === String(unitValue) || 
      opt.label?.toLowerCase() === String(unitValue).toLowerCase()
    );
    if (foundByLabel) {
      return foundByLabel.label;
    }
    
    return unitValue;
  };

  // Helper function to get currency symbol
  const getCurrencySymbol = (currencyCode) => {
    if (!currencyCode) return '';
    if (currencyOptions && currencyOptions.length > 0) {
      const foundByCode = currencyOptions.find(opt => 
        opt.currencyCode === String(currencyCode).toUpperCase() ||
        String(opt.value) === String(currencyCode) ||
        opt.currencyCode === String(currencyCode)
      );
      if (foundByCode && foundByCode.currencySymbol) {
        return foundByCode.currencySymbol;
      }
    }
    return '';
  };

  // Helper function to fetch full PO details with RFQ data
  const fetchFullPODetails = async (order) => {
    try {
      const poId = order.id || order.purchase_order_id;
      if (!poId) return order;
      
      const originalRfqId = order.rfq_id;
      
      const fullPOResponse = await CommonService.get(`/purchase-order/${poId}`);
      if (fullPOResponse && fullPOResponse.success && fullPOResponse.data) {
        const finalRfqId = originalRfqId || fullPOResponse.data.rfq_id || null;
        
        let rfqData = null;
        if (finalRfqId) {
          try {
            const rfqIdNum = typeof finalRfqId === 'string' && finalRfqId.startsWith('REQ-')
              ? parseInt(finalRfqId.replace('REQ-', ''), 10)
              : parseInt(finalRfqId, 10);
            
            if (!isNaN(rfqIdNum)) {
              const rfqResponse = await CommonService.get(`/rfq/${rfqIdNum}`);
              if (rfqResponse && rfqResponse.success && rfqResponse.data) {
                rfqData = rfqResponse.data;
              }
            }
          } catch (rfqError) {
            console.warn(`Error fetching RFQ ${finalRfqId} for PO ${poId}:`, rfqError);
          }
        }
        
        return {
          ...order,
          ...fullPOResponse.data,
          rfq_id: finalRfqId,
          rfq_data: rfqData,
          line_items: fullPOResponse.data.line_items || fullPOResponse.data.po_data?.lineItems || [],
          po_data: {
            ...order.po_data,
            ...fullPOResponse.data.po_data,
            lineItems: fullPOResponse.data.line_items || fullPOResponse.data.po_data?.lineItems || []
          }
        };
      }
      return order;
    } catch (error) {
      console.error(`Error fetching full PO details for PO ${order.id}:`, error);
      return order;
    }
  };

  // Fetch invoices for a purchase order
  const fetchInvoicesForPO = async (poId) => {
    try {
      const response = await CommonService.get(`/seller-invoice/purchase-order/${poId}`);
      if (response && response.success && response.data) {
        return Array.isArray(response.data) ? response.data : [];
      }
      return [];
    } catch (error) {
      console.warn(`Error fetching invoices for PO ${poId}:`, error);
      return [];
    }
  };

  // Fetch E-Way Bills for an invoice
  const fetchEWayBillsForInvoice = async (invoiceId) => {
    try {
      // Try to fetch E-Way Bills by invoice ID using a direct endpoint
      // If the endpoint doesn't exist, it will fail gracefully and return empty array
      const response = await CommonService.get(`/eway-bill/invoice/${invoiceId}`);
      if (response && response.success && response.data) {
        const ewayBills = Array.isArray(response.data) ? response.data : [response.data];
        console.log(`Fetched ${ewayBills.length} E-Way Bills for invoice ${invoiceId}:`, ewayBills);
        return ewayBills;
      }
      return [];
    } catch (error) {
      // If endpoint doesn't exist or fails, return empty array (Pending status)
      console.warn(`Error fetching E-Way Bills for invoice ${invoiceId}:`, error);
      return [];
    }
  };

  // Fetch accepted invoices only (marketplace queue — seller role only).
  const fetchAcceptedInvoices = async () => {
    try {
      setLoading(true);

      if (!isActiveSeller) {
        setAcceptedInvoices([]);
        try {
          const pendingRes = await CommonService.get('/eway-bill/logistics/pending');
          if (pendingRes && pendingRes.success && pendingRes.data) {
            setPendingPickupShipments(Array.isArray(pendingRes.data) ? pendingRes.data : []);
          } else {
            setPendingPickupShipments([]);
          }
        } catch {
          setPendingPickupShipments([]);
        }
        setLoading(false);
        return;
      }
      
      // This screen is reused by Seller and CM dashboards for marketplace POs.
      // Avoid 403s by selecting the correct endpoint based on current user roles.
      const roles = (() => {
        try {
          return getAvailableRoles();
        } catch {
          return [];
        }
      })();
      const hasSeller = roles.some((r) => String(r.id) === '2' || r.id === 2);
      const hasBuyer = roles.some((r) => String(r.id) === '1' || r.id === 1);
      const poPath = hasSeller
        ? "/purchase-order/seller/my-orders"
        : hasBuyer
        ? "/purchase-order/buyer/my-orders"
        : null;

      if (!poPath) {
        setAcceptedInvoices([]);
        setPendingPickupShipments([]);
        setLoading(false);
        return;
      }

      let poResponse;
      try {
        poResponse = await CommonService.get(poPath);
      } catch (e) {
        const status = e?.response?.status;
        // Some accounts may be misclassified client-side. If the chosen endpoint is forbidden,
        // retry the other endpoint once (whichever was not attempted yet).
        if (status === 403 && poPath.includes('/seller/')) {
          poResponse = await CommonService.get("/purchase-order/buyer/my-orders");
        } else if (status === 403 && poPath.includes('/buyer/')) {
          poResponse = await CommonService.get("/purchase-order/seller/my-orders");
        } else {
          throw e;
        }
      }
      
      if (poResponse && poResponse.success && poResponse.data) {
        const allOrders = Array.isArray(poResponse.data) ? poResponse.data : [];
        
        // Filter only accepted orders
        const accepted = allOrders.filter(order => {
          const status = (order.po_status || '').toLowerCase();
          const responseStatus = (order.po_response_status || '').toLowerCase();
          return status === 'accepted' || responseStatus === 'accepted';
        });
        
        // Fetch full PO details (including line items and RFQ data)
        const acceptedWithDetails = await Promise.all(
          accepted.map(order => fetchFullPODetails(order))
        );
        
        // Fetch invoices for each PO
        const ordersWithInvoices = await Promise.all(
          acceptedWithDetails.map(async (order) => {
            const poId = order.id || order.purchase_order_id;
            const invoices = poId ? await fetchInvoicesForPO(poId) : [];
            
            // Fetch E-Way Bills for each accepted invoice
            const invoicesWithEWayBills = await Promise.all(
              invoices.map(async (invoice) => {
                if (invoice.status && invoice.status.toLowerCase().trim() === 'accepted') {
                  const ewayBills = await fetchEWayBillsForInvoice(invoice.id);
                  return {
                    ...invoice,
                    ewayBills: ewayBills
                  };
                }
                return {
                  ...invoice,
                  ewayBills: []
                };
              })
            );
            
            return {
              ...order,
              invoices: invoicesWithEWayBills
            };
          })
        );
        
        // Logistics flow: only show rows after pickup is confirmed/completed.
        // Non-logistics rows have no pickup fields, so they pass through unchanged.
        const logisticsVisibilityFiltered = ordersWithInvoices.filter((order) => {
          if (!isLogisticsFlowRow(order)) return true;
          return isPickupConfirmedForLogisticsRow(order);
        });

        // Filter only orders with accepted invoices
        const acceptedInvoiceOrders = logisticsVisibilityFiltered.filter(order => {
          const invoices = order.invoices || [];
          return invoices.some(inv => {
            const status = (inv.status || '').toLowerCase().trim();
            return status === 'accepted';
          });
        });
        
        setAcceptedInvoices(acceptedInvoiceOrders);

        // Append logistics post-pickup shipments pending E-Way Bill (new enhancement).
        // Backend returns only eligible shipments and excludes ones that already have an e-way bill.
        try {
          const pendingRes = await CommonService.get('/eway-bill/logistics/pending');
          if (pendingRes && pendingRes.success && pendingRes.data) {
            setPendingPickupShipments(Array.isArray(pendingRes.data) ? pendingRes.data : []);
          } else {
            setPendingPickupShipments([]);
          }
        } catch {
          setPendingPickupShipments([]);
        }
      } else {
        setAcceptedInvoices([]);
        setPendingPickupShipments([]);
      }
    } catch (error) {
      console.error('Error fetching accepted invoices:', error);
      toast.error('Failed to fetch accepted invoices');
      setAcceptedInvoices([]);
      setPendingPickupShipments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchAcceptedInvoices();
  }, [isActiveSeller]);

  const canGenerateMarketplaceEway = (transformed) =>
    isActiveSeller &&
    transformed.invoiceId &&
    !transformed.__logistics?.quoteId;

  const canGenerateLogisticsEway = (transformed) =>
    Boolean(transformed.__logistics?.quoteId);

  const showGenerateButton = (transformed) =>
    transformed.ewayBillStatus === 'Pending' &&
    (canGenerateLogisticsEway(transformed) || canGenerateMarketplaceEway(transformed));

  const showEditDraftButton = (transformed) =>
    isActiveSeller &&
    transformed.invoiceId &&
    !transformed.__logistics?.quoteId &&
    transformed.ewayBillStatus === 'Draft' &&
    transformed.latestEWayBillId;

  // Transform order data for display
  const transformOrderData = (order) => {
    let poData = {};
    let totalAmount = 0;
    let lineItems = [];
    
    // Get accepted invoice information
    const invoices = order.invoices || [];
    const acceptedInvoice = invoices.find(inv => {
      const status = (inv.status || '').toLowerCase().trim();
      return status === 'accepted';
    }) || (invoices.length > 0 
      ? invoices.sort((a, b) => new Date(b.created_datetime || b.invoice_date) - new Date(a.created_datetime || a.invoice_date))[0]
      : null);
    
    // Determine invoice details
    let invoiceNumber = null;
    let invoiceId = null;
    let invoiceDate = null;
    let invoiceGrandTotal = null;
    let invoiceCurrency = null;
    let ewayBills = [];
    let ewayBillStatus = 'Pending'; // Default status
    let latestEWayBill = null;
    let ewayNumber = null;
    let ewayBillDate = null;
    
    if (acceptedInvoice) {
      invoiceId = acceptedInvoice.id;
      invoiceNumber = acceptedInvoice.invoice_number || null;
      invoiceDate = acceptedInvoice.invoice_date || null;
      invoiceGrandTotal = acceptedInvoice.grand_total || null;
      invoiceCurrency = acceptedInvoice.currency || null;
      ewayBills = acceptedInvoice.ewayBills || [];
      
      // Determine E-Way Bill status
      if (ewayBills && ewayBills.length > 0) {
        // Get the most recent E-Way Bill
        latestEWayBill = ewayBills.sort((a, b) => 
          new Date(b.created_datetime || b.ewb_date) - new Date(a.created_datetime || a.ewb_date)
        )[0];
        
        ewayNumber = latestEWayBill.ewb_number || null;
        ewayBillDate = latestEWayBill.ewb_date || null;
        
        const status = (latestEWayBill.status || '').trim();
        const statusLower = status.toLowerCase();
        const acceptanceDecision = (latestEWayBill.acceptance_decision || '').trim().toUpperCase();
        
        // Determine status based on E-Way Bill status and acceptance decision
        if (statusLower === 'draft') {
          ewayBillStatus = 'Draft';
        } else if (statusLower === 'active') {
          ewayBillStatus = 'E-Way Bill Created';
        } else if (statusLower === 'extended') {
          ewayBillStatus = 'Extended';
        } else {
          ewayBillStatus = status || 'Pending';
        }

        // This screen is for generation/tracking only; do not show buyer acceptance outcomes here.
        // If an E-Way Bill exists and it's not Draft, we standardize the label.
        if (ewayBillStatus !== 'Draft') {
          ewayBillStatus = 'E-Way Bill Created';
        }
      } else {
        ewayBillStatus = 'Pending';
      }
    }
    
    try {
      if (order.po_data) {
        poData = order.po_data;
        if (poData.lineItems && Array.isArray(poData.lineItems) && poData.lineItems.length > 0) {
          lineItems = poData.lineItems;
        }
      }
      if (order.line_items && Array.isArray(order.line_items) && order.line_items.length > 0) {
        lineItems = order.line_items;
      }
      
      // Also get line items from invoice if available
      if (acceptedInvoice && acceptedInvoice.line_items && Array.isArray(acceptedInvoice.line_items)) {
        lineItems = acceptedInvoice.line_items;
      }
      
      if (lineItems && lineItems.length > 0) {
        totalAmount = lineItems.reduce((sum, item) => {
          if (item.total !== undefined && item.total !== null && item.total !== '') {
            const itemTotal = parseFloat(item.total);
            if (!isNaN(itemTotal) && itemTotal > 0) {
              return sum + itemTotal;
            }
          }
          
          const unitPrice = parseFloat(item.unit_price || item.unitPrice) || 0;
          const quantity = parseFloat(item.quantity) || 0;
          const discount = parseFloat(item.discount) || 0;
          const subtotal = unitPrice * quantity;
          const discountAmount = subtotal * (discount / 100);
          const taxableAmount = subtotal - discountAmount;
          
          const cgstAmount = parseFloat(item.cgst_amount || item.cgstAmount) || 0;
          const sgstAmount = parseFloat(item.sgst_amount || item.sgstAmount) || 0;
          const igstAmount = parseFloat(item.igst_amount || item.igstAmount) || 0;
          const cessAmount = parseFloat(item.cess_amount || item.cessAmount) || 0;
          const customsAmount = parseFloat(item.customs_amount || item.customsAmount) || 0;
          const bcdAmount = parseFloat(item.bcd_amount || item.bcdAmount) || 0;
          const swsAmount = parseFloat(item.sws_amount || item.swsAmount) || 0;
          const taxAmount = parseFloat(item.tax_amount || item.taxAmount) || 0;
          
          if (item.assessable_value !== undefined && item.assessable_value !== null && item.assessable_value !== '') {
            const assessableValue = parseFloat(item.assessable_value || item.assessableValue) || 0;
            const igstOnAssessable = parseFloat(item.igst_amount || item.igstAmount) || 0;
            const cessOnAssessable = parseFloat(item.cess_amount || item.cessAmount) || 0;
            return sum + (assessableValue + igstOnAssessable + cessOnAssessable);
          }
          
          return sum + (taxableAmount + cgstAmount + sgstAmount + igstAmount + cessAmount + customsAmount + bcdAmount + swsAmount + taxAmount);
        }, 0);
      }
    } catch (e) {
      console.warn('Error parsing PO data:', e);
    }
    
    // Use invoice currency if available, otherwise use PO currency
    const currency = invoiceCurrency || poData.currency || order.currency || 'INR';
    const currencyCode = getCurrencyLabel(currency);
    
    // Use invoice grand_total if available, otherwise calculate from line items
    const displayTotalAmount = invoiceGrandTotal !== null && invoiceGrandTotal !== undefined 
      ? invoiceGrandTotal 
      : totalAmount;
    
    // Extract product names - prioritize RFQ data
    let productNames = [];
    if (order.rfq_data && order.rfq_data.product_name) {
      const rfqProductName = order.rfq_data.product_name.trim();
      if (rfqProductName && rfqProductName !== '' && rfqProductName !== 'N/A') {
        let cleanedProductName = rfqProductName
          .replace(/\bHSN\s*[:\-]?\s*\d{4,8}\b/gi, '')
          .replace(/\b\d{4,8}\s*\(HSN\)/gi, '')
          .replace(/\bHSN\s*Code\s*[:\-]?\s*\d{4,8}\b/gi, '')
          .trim();
        cleanedProductName = cleanedProductName.replace(/\s+/g, ' ').trim();
        if (cleanedProductName && cleanedProductName !== '') {
          productNames.push(cleanedProductName);
        }
      }
    }
    
    if (productNames.length === 0 && lineItems && lineItems.length > 0) {
      lineItems.forEach(item => {
        let productName = item.description || item.productName || item.product_name || item.name || '';
        if (productName) {
          productName = productName
            .replace(/\bHSN\s*[:\-]?\s*\d{4,8}\b/gi, '')
            .replace(/\b\d{4,8}\s*\(HSN\)/gi, '')
            .replace(/\bHSN\s*Code\s*[:\-]?\s*\d{4,8}\b/gi, '')
            .trim();
          productName = productName.replace(/\s+/g, ' ').trim();
        }
        if (productName && productName.trim() !== '' && productName !== 'N/A') {
          if (!productNames.includes(productName.trim())) {
            productNames.push(productName.trim());
          }
        }
      });
    }
    
    const productNamesDisplay = productNames.length > 0 
      ? (productNames.length > 3 ? productNames.slice(0, 3).join(', ') + ` +${productNames.length - 3} more` : productNames.join(', '))
      : 'N/A';
    
    // Extract product categories
    let productCategories = [];
    if (lineItems && lineItems.length > 0) {
      lineItems.forEach(item => {
        const category = item.category || item.productCategory || item.product_category || item.category_name || '';
        if (category && category.trim() !== '' && !productCategories.includes(category.trim())) {
          productCategories.push(category.trim());
        }
      });
    }
    
    if (productCategories.length === 0) {
      const rfqData = order.rfq_data || order.rfqData;
      if (rfqData) {
        const categoryName = rfqData.category_name || rfqData.categoryName || '';
        if (categoryName && categoryName.trim() !== '') {
          productCategories.push(categoryName.trim());
        }
      }
    }
    
    const productCategoriesDisplay = productCategories.length > 0 
      ? productCategories.join(', ')
      : 'N/A';
    
    // Calculate quantity from line items
    let totalQuantity = 0;
    let quantityUnit = '';
    if (lineItems && lineItems.length > 0) {
      lineItems.forEach(item => {
        const itemQuantity = parseFloat(item.quantity) || 0;
        totalQuantity += itemQuantity;
        // Check both 'unit' (from invoice) and 'uom' (from PO) field names
        if (!quantityUnit) {
          const unitValue = item.unit || item.uom || item.unit_of_measurement;
          if (unitValue) {
            quantityUnit = getUnitLabel(unitValue);
          }
        }
      });
    }
    
    const quantityDisplay = totalQuantity > 0 
      ? `${totalQuantity} ${quantityUnit || ''}`.trim()
      : 'N/A';
    
    // For Draft and E-Way Bill Created status, use E-Way Bill Date; otherwise use invoice date or PO date
    const displayDate = ((ewayBillStatus === 'Draft' || ewayBillStatus === 'E-Way Bill Created') && ewayBillDate) 
      ? formatDate(ewayBillDate) 
      : (invoiceDate ? formatDate(invoiceDate) : formatDate(order.po_date));
    
    return {
      id: order.id || order.purchase_order_id,
      poNumber: order.po_number || `PO-${order.id}`,
      poDate: displayDate,
      ewayBillDate: ewayBillDate ? formatDate(ewayBillDate) : null,
      productName: productNamesDisplay,
      category: productCategoriesDisplay,
      quantity: quantityDisplay,
      totalAmount: formatCurrency(displayTotalAmount, currencyCode),
      expectedDelivery: poData.expectedDeliveryDate ? formatDate(poData.expectedDeliveryDate) : (order.expected_delivery_date ? formatDate(order.expected_delivery_date) : 'N/A'),
      invoiceNumber: invoiceNumber,
      invoiceId: invoiceId,
      lineItems: lineItems,
      originalData: order,
      invoiceData: acceptedInvoice,
      ewayBillStatus: ewayBillStatus,
      ewayBills: ewayBills,
      latestEWayBillId: latestEWayBill ? latestEWayBill.id : null,
      latestEWayBill: latestEWayBill,
      ewayNumber: ewayNumber
    };
  };

  const getCurrencyLabel = (currencyValue) => {
    if (!currencyValue) return 'INR';
    if (!currencyOptions || currencyOptions.length === 0) return currencyValue;
    
    const foundByValue = currencyOptions.find(opt => 
      String(opt.value) === String(currencyValue) ||
      opt.currencyCode === String(currencyValue)
    );
    if (foundByValue) {
      return foundByValue.currencyCode || foundByValue.label || currencyValue;
    }
    
    const foundByCode = currencyOptions.find(opt => 
      opt.currencyCode === String(currencyValue).toUpperCase()
    );
    if (foundByCode) {
      return foundByCode.currencyCode || foundByCode.label || currencyValue;
    }
    
    return currencyValue;
  };

  const formatCurrency = (amount, currencyCode) => {
    if (!amount && amount !== 0) return 'N/A';
    
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return 'N/A';
    
    const formatted = numAmount.toLocaleString('en-IN', {
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    });
    
    const symbol = getCurrencySymbol(currencyCode);
    
    return `${symbol}${formatted}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Get E-Way Bill details
  const getEWayBillDetails = async (ewayBillId) => {
    try {
      const response = await CommonService.get(`/eway-bill/${ewayBillId}`);
      if (response && response.success && response.data) {
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching E-Way Bill details:', error);
      toast.error('Failed to fetch E-Way Bill details');
      return null;
    }
  };

  const handleViewEWayBill = async (ewayBillId) => {
    const ewayBillDetails = await getEWayBillDetails(ewayBillId);
    if (ewayBillDetails) {
      setSelectedEWayBill(ewayBillDetails);
      setIsModalOpen(true);
    }
  };

  // If navigated from logistics flow "View E-Way Bill", auto-open the details modal.
  useEffect(() => {
    const openId = location?.state?.openEWayBillId;
    if (openId) {
      handleViewEWayBill(openId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.state?.openEWayBillId]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedEWayBill(null);
  };

  // Handle Update Vehicle - Open modal with E-Way Bill details
  const handleUpdateVehicle = async (ewayBillId) => {
    const ewayBillDetails = await getEWayBillDetails(ewayBillId);
    if (ewayBillDetails) {
      setSelectedEWayBill(ewayBillDetails);
      setIsUpdateVehicleModalOpen(true);
      setVehicleUpdateForm({
        newVehicleNumber: '',
        currentLocation: '',
        reasonForChange: ''
      });
    } else {
      toast.error('Failed to load E-Way Bill details for vehicle update');
    }
  };

  // Handle Close Update Vehicle Modal
  const handleCloseUpdateVehicleModal = () => {
    setIsUpdateVehicleModalOpen(false);
    setSelectedEWayBill(null);
    setVehicleUpdateForm({
      newVehicleNumber: '',
      currentLocation: '',
      reasonForChange: ''
    });
  };

  // Handle Vehicle Update Form Change
  const handleVehicleUpdateFormChange = (field, value) => {
    setVehicleUpdateForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle Submit Vehicle Update
  const handleSubmitVehicleUpdate = async () => {
    // Validate required fields
    if (!vehicleUpdateForm.newVehicleNumber || !vehicleUpdateForm.currentLocation || !vehicleUpdateForm.reasonForChange) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!selectedEWayBill?.id) {
      toast.error('E-Way Bill ID is missing for vehicle update.');
      return;
    }

    // Validate vehicle number format (basic validation)
    const vehicleNumber = vehicleUpdateForm.newVehicleNumber.trim();
    if (vehicleNumber.length < 3) {
      toast.error('Please enter a valid vehicle number');
      return;
    }

    try {
      setLoading(true);

      // Prepare update data - only update vehicle-related fields
      // Preserve existing status and other important fields
      const updateData = {
        vehicle_number: vehicleNumber,
        previous_vehicle_number: selectedEWayBill.vehicle_number || null, // Store previous vehicle number
        vehicle_update_location: vehicleUpdateForm.currentLocation.trim(), // Store location in dedicated column
        vehicle_update_reason: vehicleUpdateForm.reasonForChange.trim(), // Store reason in dedicated column
        vehicle_updated_by_user_id: null, // Will be set by backend from auth token
        status: selectedEWayBill.status || 'Active' // Preserve existing status
        // Note: vehicle_updated_at will be automatically set to CURRENT_TIMESTAMP by the database
      };

      const response = await CommonService.put(`/eway-bill/${selectedEWayBill.id}`, updateData);

      if (response && response.success) {
        toast.success('Vehicle details updated successfully');
        handleCloseUpdateVehicleModal();
        // Refresh the list to show updated vehicle number
        await fetchAcceptedInvoices();
      } else {
        toast.error(response?.error || 'Failed to update vehicle details.');
      }
    } catch (error) {
      console.error('Error updating vehicle details:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to update vehicle details.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Filter orders by search query
  const filteredOrders = acceptedInvoices.filter(order => {
    if (!searchQuery) return true;
    const transformed = transformOrderData(order);
    const query = searchQuery.toLowerCase();
    return (
      (transformed.poNumber && transformed.poNumber.toLowerCase().includes(query)) ||
      (transformed.invoiceNumber && transformed.invoiceNumber.toLowerCase().includes(query)) ||
      (transformed.productName && transformed.productName.toLowerCase().includes(query)) ||
      (transformed.category && transformed.category.toLowerCase().includes(query))
    );
  });

  // Map logistics post-pickup shipments to the same card model (minimal fields).
  const pickupShipmentCards = (pendingPickupShipments || [])
    .map((s) => {
      const ref = s?.rfq_reference_number || s?.rfqReferenceNumber || '—';
      const quoteId = s?.logistics_quote_id || s?.logisticsQuoteId || null;
      const submissionId = s?.logistics_generic_rfq_id || s?.logisticsGenericRfqId || null;
      const pickupConfirmedAt = s?.pickup_confirmed_at || s?.pickupConfirmedAt || null;
      return {
        id: `logistics-${submissionId || 'x'}-${quoteId || 'x'}`,
        poNumber: ref,
        poDate: pickupConfirmedAt ? formatDate(pickupConfirmedAt) : 'N/A',
        productName: 'N/A',
        category: 'N/A',
        quantity: 'N/A',
        totalAmount: 'N/A',
        expectedDelivery: 'N/A',
        invoiceNumber: null,
        invoiceId: null,
        ewayBillStatus: 'Pending',
        latestEWayBillId: null,
        latestEWayBill: null,
        ewayNumber: null,
        __logistics: { quoteId, submissionId },
      };
    })
    .filter((card) => {
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return String(card.poNumber || '').toLowerCase().includes(q);
    });

  const combinedCards = [
    ...filteredOrders.map((o) => ({ kind: 'invoice', raw: o })),
    ...pickupShipmentCards.map((c) => ({ kind: 'logistics', card: c })),
  ];

  return (
    <div className="invoice-list">
      <div className="il-main-content">
        {/* Header Section */}
        <div className="il-header">
          <div className="il-header-left">
            <nav aria-label="breadcrumb" className="il-breadcrumb">
              <ol className="il-breadcrumb-list">
                <li className="il-breadcrumb-item">
                  <Link to="/dashboard/seller" className="il-breadcrumb-link">Home</Link>
                </li>
                <li className="il-breadcrumb-item il-breadcrumb-active" aria-current="page">
                  E-Way Bill Generation
                </li>
              </ol>
            </nav>
            <h1 className="il-main-title">E-Way Bill Generation</h1>
            <p className="il-subtitle">
              {isActiveSeller
                ? 'Generate E-Way Bills for accepted marketplace invoices'
                : 'Generate E-Way Bills for logistics shipments after pickup. For marketplace orders, use Track e-way bills.'}
            </p>
          </div>
          <div className="il-header-right">
            <button className="il-back-btn" onClick={() => navigate('/dashboard/seller')}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m12 19-7-7 7-7"></path>
                <path d="M19 12H5"></path>
              </svg>
              Back
            </button>
          </div>
        </div>

        {/* Search Section */}
        <div className="il-search-filter-section">
          <div className="il-search-header">
            <h2 className="il-search-title">Search Accepted Invoices</h2>
          </div>
          <div className="il-search-filter-row">
            <div className="il-search-wrapper">
              <svg 
                className="il-search-icon" 
                xmlns="http://www.w3.org/2000/svg" 
                width="20" 
                height="20" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                className="il-search-input"
                placeholder="Search by invoice number, PO number, product name, or category..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Invoice Cards */}
        <div className="il-invoices-container">
          {loading ? (
            <div className="il-empty-state">
              <p>Loading accepted invoices...</p>
            </div>
          ) : combinedCards.length === 0 ? (
            <div className="il-empty-state">
              <p>
                {isActiveSeller
                  ? 'No accepted invoices found.'
                  : 'No logistics shipments pending E-Way Bill generation.'}
              </p>
              {!isActiveSeller ? (
                <p className="text-muted small mt-2">
                  Marketplace purchase orders are tracked under{' '}
                  <Link to="/eway-bill-tracking">Track e-way bills</Link>.
                </p>
              ) : null}
            </div>
          ) : (
            combinedCards.map((row) => {
              const transformed = row.kind === 'invoice' ? transformOrderData(row.raw) : row.card;
              return (
                <div key={transformed.id} className="il-invoice-card">
                  <div className="il-card-content-wrapper">
                    <div className="il-card-main-row">
                      <div className="il-card-left-section">
                        <div className="il-invoice-header-row">
                          <div className="il-invoice-header-left">
                            <svg 
                              className="il-document-icon"
                              xmlns="http://www.w3.org/2000/svg" 
                              width="24" 
                              height="24" 
                              viewBox="0 0 24 24" 
                              fill="none" 
                              stroke="currentColor" 
                              strokeWidth="2" 
                              strokeLinecap="round" 
                              strokeLinejoin="round"
                            >
                              <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                              <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                              <path d="M10 9H8"></path>
                              <path d="M16 13H8"></path>
                              <path d="M16 17H8"></path>
                            </svg>
                            <div className="il-invoice-title-group">
                              <div className="il-title-row">
                                <h3 className="il-invoice-number">
                                  {/* For Draft, E-Way Bill Created, Accepted, and Rejected status, show E-Way Bill Number as primary identifier */}
                                  {(transformed.ewayBillStatus === 'Draft' || transformed.ewayBillStatus === 'E-Way Bill Created') && transformed.ewayNumber 
                                    ? transformed.ewayNumber 
                                    : (transformed.invoiceNumber || transformed.poNumber)}
                                </h3>
                                <span className={`il-status-badge ${
                                  transformed.ewayBillStatus === 'Draft' 
                                    ? 'il-status-draft' 
                                    : transformed.ewayBillStatus === 'E-Way Bill Created' 
                                    ? 'il-status-accepted' 
                                    : 'il-status-pending'
                                }`}>
                                  {transformed.ewayBillStatus}
                                </span>
                              </div>
                              {/* For Draft and E-Way Bill Created status: Show Invoice Number as sub-detail */}
                              {(transformed.ewayBillStatus === 'Draft' || transformed.ewayBillStatus === 'E-Way Bill Created') && transformed.invoiceNumber && (
                                <p className="il-invoice-number-subtitle">
                                  Invoice: <span className="il-invoice-number-value">{transformed.invoiceNumber}</span>
                                </p>
                              )}
                              {/* For Pending status: Show PO as sub-detail if invoice exists */}
                              {transformed.ewayBillStatus === 'Pending' && transformed.invoiceNumber && (
                                <p className="il-invoice-number-subtitle">
                                  PO: <span className="il-invoice-number-value">{transformed.poNumber}</span>
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="il-card-actions">
                            {/* Generate E-Way Bill — seller for marketplace; buyer for logistics only */}
                            {showGenerateButton(transformed) && (
                              <button 
                                className="il-action-btn il-btn-eway"
                                onClick={() => navigate('/eway-bill-generate', { 
                                  state: { 
                                    invoiceId: transformed.invoiceId,
                                    invoiceNumber: transformed.invoiceNumber,
                                    logisticsQuoteId: transformed.__logistics?.quoteId || null,
                                    logisticsSubmissionId: transformed.__logistics?.submissionId || null,
                                    logisticsReferenceNumber: transformed.poNumber || null,
                                  } 
                                })}
                              >
                                <svg 
                                  xmlns="http://www.w3.org/2000/svg" 
                                  width="16" 
                                  height="16" 
                                  viewBox="0 0 24 24" 
                                  fill="none" 
                                  stroke="currentColor" 
                                  strokeWidth="2" 
                                  strokeLinecap="round" 
                                  strokeLinejoin="round"
                                >
                                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                                  <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                                  <line x1="12" y1="22.08" x2="12" y2="12"></line>
                                </svg>
                                Generate E-Way Number
                              </button>
                            )}
                            
                            {/* Edit E-Way Number — seller marketplace drafts only */}
                            {showEditDraftButton(transformed) && (
                              <button 
                                className="il-action-btn il-btn-edit"
                                onClick={() => navigate('/eway-bill-generate', { 
                                  state: { 
                                    invoiceId: transformed.invoiceId,
                                    invoiceNumber: transformed.invoiceNumber,
                                    ewayBillId: transformed.latestEWayBillId
                                  } 
                                })}
                              >
                                <svg 
                                  xmlns="http://www.w3.org/2000/svg" 
                                  width="16" 
                                  height="16" 
                                  viewBox="0 0 24 24" 
                                  fill="none" 
                                  stroke="currentColor" 
                                  strokeWidth="2" 
                                  strokeLinecap="round" 
                                  strokeLinejoin="round"
                                >
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                </svg>
                                Edit E-Way Number
                              </button>
                            )}

                            {/* View E-Way Bill Details and Update Vehicle buttons - for E-Way Bill Created (Active) and Extended status */}
                            {(transformed.ewayBillStatus === 'E-Way Bill Created' || transformed.ewayBillStatus === 'Extended') && transformed.latestEWayBillId && (
                              <>
                                <button 
                                  className="il-action-btn il-btn-view"
                                  onClick={() => handleViewEWayBill(transformed.latestEWayBillId)}
                                >
                                  <svg 
                                    xmlns="http://www.w3.org/2000/svg" 
                                    width="16" 
                                    height="16" 
                                    viewBox="0 0 24 24" 
                                    fill="none" 
                                    stroke="currentColor" 
                                    strokeWidth="2" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round"
                                  >
                                    <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"></path>
                                    <circle cx="12" cy="12" r="3"></circle>
                                  </svg>
                                  View E-Way Bill Details
                                </button>
                                <button 
                                  className="il-action-btn il-btn-edit"
                                  onClick={() => handleUpdateVehicle(transformed.latestEWayBillId)}
                                >
                                  <svg 
                                    xmlns="http://www.w3.org/2000/svg" 
                                    width="16" 
                                    height="16" 
                                    viewBox="0 0 24 24" 
                                    fill="none" 
                                    stroke="currentColor" 
                                    strokeWidth="2" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round"
                                  >
                                    <path d="M12 20h9"></path>
                                    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
                                  </svg>
                                  Update Vehicle
                                </button>
                              </>
                            )}

                            {/* For Accepted/Rejected: view-only (no accept/reject info needed here) */}
                            {(transformed.ewayBillStatus === 'Accepted' || transformed.ewayBillStatus === 'Rejected') && transformed.latestEWayBillId && (
                              <button
                                className="il-action-btn il-btn-view"
                                onClick={() => handleViewEWayBill(transformed.latestEWayBillId)}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"></path>
                                  <circle cx="12" cy="12" r="3"></circle>
                                </svg>
                                View E-Way Bill Details
                              </button>
                            )}
                          </div>
                        </div>

                        <div className="il-details-grid">
                          <div className="il-grid-item">
                            <p className="il-detail-label">
                              {(transformed.ewayBillStatus === 'Draft' || transformed.ewayBillStatus === 'E-Way Bill Created' || transformed.ewayBillStatus === 'Accepted' || transformed.ewayBillStatus === 'Rejected') ? 'E-Way Bill Date' : 'Invoice Date'}
                            </p>
                            <p className="il-detail-value">{transformed.poDate}</p>
                          </div>
                          <div className="il-grid-item">
                            <p className="il-detail-label">Product Name</p>
                            <p className="il-detail-value" title={transformed.productName}>{transformed.productName}</p>
                          </div>
                          <div className="il-grid-item">
                            <p className="il-detail-label">Category</p>
                            <p className="il-detail-value">{transformed.category}</p>
                          </div>
                          <div className="il-grid-item">
                            <p className="il-detail-label">Quantity</p>
                            <p className="il-detail-value">{transformed.quantity}</p>
                          </div>
                          <div className="il-grid-item">
                            <p className="il-detail-label">Total Amount</p>
                            <p className="il-detail-value">{transformed.totalAmount}</p>
                          </div>
                          <div className="il-grid-item">
                            <p className="il-detail-label">Expected Delivery Date</p>
                            <p className="il-detail-value">{transformed.expectedDelivery}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* E-Way Bill Details Modal */}
      {isModalOpen && selectedEWayBill && (
        <div className="il-modal-overlay" onClick={handleCloseModal}>
          <div
            className="il-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="il-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              className="il-modal-close" 
              onClick={handleCloseModal}
              aria-label="Close E-Way Bill details"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18"></path>
                <path d="m6 6 12 12"></path>
              </svg>
            </button>

            <div className="il-modal-header">
              <h2 id="il-modal-title" className="il-modal-title">
                E-Way Bill Details - {selectedEWayBill.ewb_number}
              </h2>
              <p className="il-modal-subtitle">Complete E-Way Bill information with all transportation details</p>
            </div>

            <div className="il-modal-content">
              {/* Summary Grid */}
              <div className="il-summary-grid">
                <div className="il-summary-item">
                  <label className="il-summary-label">E-Way Bill Number</label>
                  <p className="il-summary-value">{selectedEWayBill.ewb_number || '—'}</p>
                </div>
                <div className="il-summary-item">
                  <label className="il-summary-label">E-Way Bill Date</label>
                  <p className="il-summary-value">{formatDateForDisplay(selectedEWayBill.ewb_date)}</p>
                </div>
                <div className="il-summary-item">
                  <label className="il-summary-label">Valid Upto</label>
                  <p className="il-summary-value">{formatDateForDisplay(selectedEWayBill.valid_upto)}</p>
                </div>
                <div className="il-summary-item">
                  <label className="il-summary-label">Document Number</label>
                  <p className="il-summary-value">{selectedEWayBill.document_number || '—'}</p>
                </div>
                <div className="il-summary-item">
                  <label className="il-summary-label">Document Date</label>
                  <p className="il-summary-value">{formatDateForDisplay(selectedEWayBill.document_date)}</p>
                </div>
                <div className="il-summary-item">
                  <label className="il-summary-label">Status</label>
                  <span className={`il-status-badge ${
                    selectedEWayBill.status === 'Draft' 
                      ? 'il-status-draft' 
                      : selectedEWayBill.status === 'Active' 
                      ? 'il-status-accepted' 
                      : selectedEWayBill.status === 'Accepted'
                      ? 'il-status-accepted'
                      : selectedEWayBill.status === 'Rejected'
                      ? 'il-status-rejected'
                      : 'il-status-pending'
                  }`}>
                    {selectedEWayBill.status === 'Active' 
                      ? 'E-Way Bill Created' 
                      : selectedEWayBill.status === 'Accepted'
                      ? 'Accepted'
                      : selectedEWayBill.status === 'Rejected'
                      ? 'Rejected'
                      : selectedEWayBill.status || 'Pending'}
                  </span>
                </div>
              </div>

              {/* Supplier/Consignor (From) Details */}
              <div className="il-modal-section">
                <h3 className="il-section-title">Supplier / Consignor (From)</h3>
                <div className="il-section-card">
                  <div className="il-section-grid">
                    <div>
                      <label className="il-section-label">Name</label>
                      <p className="il-section-value">{selectedEWayBill.from_name || '—'}</p>
                    </div>
                    <div>
                      <label className="il-section-label">GSTIN</label>
                      <p className="il-section-value">{selectedEWayBill.from_gstin || '—'}</p>
                    </div>
                    <div>
                      <label className="il-section-label">Country</label>
                      <p className="il-section-value">{selectedEWayBill.from_country || '—'}</p>
                    </div>
                    <div>
                      <label className="il-section-label">State</label>
                      <p className="il-section-value">{selectedEWayBill.from_state || '—'}</p>
                    </div>
                    <div>
                      <label className="il-section-label">City</label>
                      <p className="il-section-value">{selectedEWayBill.from_city || '—'}</p>
                    </div>
                    <div>
                      <label className="il-section-label">Pincode</label>
                      <p className="il-section-value">{selectedEWayBill.from_pincode || '—'}</p>
                    </div>
                    <div style={{ gridColumn: 'span 6' }}>
                      <label className="il-section-label">Address</label>
                      <p className="il-section-value">{selectedEWayBill.from_address || '—'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Buyer/Consignee (To) Details */}
              <div className="il-modal-section">
                <h3 className="il-section-title">Buyer / Consignee (To)</h3>
                <div className="il-section-card">
                  <div className="il-section-grid">
                    <div>
                      <label className="il-section-label">Name</label>
                      <p className="il-section-value">{selectedEWayBill.to_name || '—'}</p>
                    </div>
                    <div>
                      <label className="il-section-label">GSTIN</label>
                      <p className="il-section-value">{selectedEWayBill.to_gstin || 'N/A (International)'}</p>
                    </div>
                    <div>
                      <label className="il-section-label">Country</label>
                      <p className="il-section-value">{selectedEWayBill.to_country || '—'}</p>
                    </div>
                    <div>
                      <label className="il-section-label">State</label>
                      <p className="il-section-value">{selectedEWayBill.to_state || '—'}</p>
                    </div>
                    <div>
                      <label className="il-section-label">City</label>
                      <p className="il-section-value">{selectedEWayBill.to_city || '—'}</p>
                    </div>
                    <div>
                      <label className="il-section-label">Pincode</label>
                      <p className="il-section-value">{selectedEWayBill.to_pincode || '—'}</p>
                    </div>
                    <div style={{ gridColumn: 'span 6' }}>
                      <label className="il-section-label">Address</label>
                      <p className="il-section-value">{selectedEWayBill.to_address || '—'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Transaction Details */}
              <div className="il-modal-section">
                <h3 className="il-section-title">Transaction Details</h3>
                <div className="il-section-card">
                  <div className="il-section-grid">
                    <div>
                      <label className="il-section-label">Supply Type</label>
                      <p className="il-section-value">{selectedEWayBill.supply_type || '—'}</p>
                    </div>
                    <div>
                      <label className="il-section-label">Sub Supply Type</label>
                      <p className="il-section-value">{selectedEWayBill.sub_supply_type || '—'}</p>
                    </div>
                    <div>
                      <label className="il-section-label">Transaction Type</label>
                      <p className="il-section-value">{selectedEWayBill.transaction_type || '—'}</p>
                    </div>
                    <div>
                      <label className="il-section-label">Main HSN Code</label>
                      <p className="il-section-value">{selectedEWayBill.main_hsn_code || '—'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Transportation Details */}
              <div className="il-modal-section">
                <h3 className="il-section-title">Transportation Details</h3>
                <div className="il-section-card">
                  <div className="il-section-grid">
                    <div>
                      <label className="il-section-label">Transport Mode</label>
                      <p className="il-section-value">{selectedEWayBill.transport_mode || '—'}</p>
                    </div>
                    <div>
                      <label className="il-section-label">Distance (KM)</label>
                      <p className="il-section-value">{selectedEWayBill.distance_km || '—'}</p>
                    </div>
                    <div>
                      <label className="il-section-label">Vehicle Type</label>
                      <p className="il-section-value">{selectedEWayBill.vehicle_type || '—'}</p>
                    </div>
                    <div>
                      <label className="il-section-label">Vehicle Number</label>
                      <p className="il-section-value">{selectedEWayBill.vehicle_number || '—'}</p>
                    </div>
                    {selectedEWayBill.vehicle_type_description && (
                      <div>
                        <label className="il-section-label">Vehicle Type Description</label>
                        <p className="il-section-value">{selectedEWayBill.vehicle_type_description}</p>
                      </div>
                    )}
                    {selectedEWayBill.reason_for_transportation && (
                      <div>
                        <label className="il-section-label">Reason for Transportation</label>
                        <p className="il-section-value">{selectedEWayBill.reason_for_transportation}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Vehicle Update Information (Part-B) */}
              {(selectedEWayBill.previous_vehicle_number || selectedEWayBill.vehicle_update_location || selectedEWayBill.vehicle_update_reason || selectedEWayBill.vehicle_updated_at) && (
                <div className="il-modal-section">
                  <h3 className="il-section-title">Vehicle Update Information (Part-B)</h3>
                  <div className="il-section-card">
                    <div className="il-section-grid">
                      {selectedEWayBill.previous_vehicle_number && (
                        <div>
                          <label className="il-section-label">Previous Vehicle Number</label>
                          <p className="il-section-value">{selectedEWayBill.previous_vehicle_number}</p>
                        </div>
                      )}
                      <div>
                        <label className="il-section-label">Current Vehicle Number</label>
                        <p className="il-section-value">{selectedEWayBill.vehicle_number || '—'}</p>
                      </div>
                      {selectedEWayBill.vehicle_update_location && (
                        <div>
                          <label className="il-section-label">Update Location</label>
                          <p className="il-section-value">{selectedEWayBill.vehicle_update_location}</p>
                        </div>
                      )}
                      {selectedEWayBill.vehicle_updated_at && (
                        <div>
                          <label className="il-section-label">Updated At</label>
                          <p className="il-section-value">{new Date(selectedEWayBill.vehicle_updated_at).toLocaleString()}</p>
                        </div>
                      )}
                      {selectedEWayBill.vehicle_update_reason && (
                        <div style={{ gridColumn: 'span 4' }}>
                          <label className="il-section-label">Reason for Vehicle Change</label>
                          <p className="il-section-value">{selectedEWayBill.vehicle_update_reason}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Transporter Details */}
              {(selectedEWayBill.transporter_name || selectedEWayBill.transporter_gstin || selectedEWayBill.transport_document_no) && (
                <div className="il-modal-section">
                  <h3 className="il-section-title">Transporter Details</h3>
                  <div className="il-section-card">
                    <div className="il-section-grid">
                      {selectedEWayBill.transporter_name && (
                        <div>
                          <label className="il-section-label">Transporter Name</label>
                          <p className="il-section-value">{selectedEWayBill.transporter_name}</p>
                        </div>
                      )}
                      {selectedEWayBill.transporter_gstin && (
                        <div>
                          <label className="il-section-label">Transporter GSTIN</label>
                          <p className="il-section-value">{selectedEWayBill.transporter_gstin}</p>
                        </div>
                      )}
                      {selectedEWayBill.transport_document_no && (
                        <div>
                          <label className="il-section-label">Transport Document No.</label>
                          <p className="il-section-value">{selectedEWayBill.transport_document_no}</p>
                        </div>
                      )}
                      {selectedEWayBill.transport_document_date && (
                        <div>
                          <label className="il-section-label">Transport Document Date</label>
                          <p className="il-section-value">{formatDateForDisplay(selectedEWayBill.transport_document_date)}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Remarks */}
              {selectedEWayBill.remarks && (
                <div className="il-modal-section">
                  <h3 className="il-section-title">Remarks</h3>
                  <div className="il-section-card">
                    <p className="il-section-value">{selectedEWayBill.remarks}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="il-modal-footer">
              <button 
                className="il-btn il-btn-secondary"
                onClick={handleCloseModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Update Vehicle Details Modal */}
      {isUpdateVehicleModalOpen && selectedEWayBill && (
        <div className="il-modal-overlay" onClick={handleCloseUpdateVehicleModal}>
          <div
            className="il-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="il-update-vehicle-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              className="il-modal-close" 
              onClick={handleCloseUpdateVehicleModal}
              aria-label="Close update vehicle modal"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18"></path>
                <path d="m6 6 12 12"></path>
              </svg>
            </button>

            <div className="il-modal-header">
              <h2 id="il-update-vehicle-modal-title" className="il-modal-title">
                Update Vehicle Details - Part B
              </h2>
              <p className="il-modal-subtitle">Update vehicle information for E-way Bill {selectedEWayBill.ewb_number}</p>
            </div>

            <div className="il-modal-content">
              {/* Current Vehicle Information */}
              <div className="il-modal-section">
                <div className="il-vehicle-current-box">
                  <div className="il-vehicle-current-row">
                    <label className="il-vehicle-current-label">Current Vehicle:</label>
                    <span className="il-vehicle-current-value">
                      {selectedEWayBill.vehicle_number || '—'}
                    </span>
                  </div>
                  <p className="il-vehicle-current-description">
                    You can update Part-B (vehicle details) multiple times during transit
                  </p>
                </div>
              </div>

              {/* Form Fields */}
              <div className="il-modal-section">
                <div className="il-vehicle-form-row">
                  <div className="il-acceptance-form-field">
                    <label className="il-acceptance-field-label">
                      New Vehicle Number <span className="il-required">*</span>
                    </label>
                    <input
                      type="text"
                      className="il-acceptance-input"
                      placeholder="e.g., KA01AB1234"
                      value={vehicleUpdateForm.newVehicleNumber}
                      onChange={(e) => handleVehicleUpdateFormChange('newVehicleNumber', e.target.value)}
                    />
                  </div>

                  <div className="il-acceptance-form-field">
                    <label className="il-acceptance-field-label">
                      Current Location / Place <span className="il-required">*</span>
                    </label>
                    <input
                      type="text"
                      className="il-acceptance-input"
                      placeholder="Where is the vehicle currently?"
                      value={vehicleUpdateForm.currentLocation}
                      onChange={(e) => handleVehicleUpdateFormChange('currentLocation', e.target.value)}
                    />
                  </div>
                </div>

                <div className="il-acceptance-form-field">
                  <label className="il-acceptance-field-label">
                    Reason for Vehicle Change <span className="il-required">*</span>
                  </label>
                  <textarea
                    className="il-acceptance-textarea"
                    placeholder="e.g., Vehicle breakdown, transshipment, etc."
                    value={vehicleUpdateForm.reasonForChange}
                    onChange={(e) => handleVehicleUpdateFormChange('reasonForChange', e.target.value)}
                    rows="3"
                  />
                </div>
              </div>

              {/* Important Notes */}
              <div className="il-modal-section">
                <div className="il-vehicle-notes-box">
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    width="20" 
                    height="20" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                    className="il-vehicle-notes-icon"
                  >
                    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path>
                    <path d="M12 9v4"></path>
                    <path d="M12 17h.01"></path>
                  </svg>
                  <div>
                    <p className="il-vehicle-notes-title">
                      Important Notes:
                    </p>
                    <ul className="il-vehicle-notes-list">
                      <li>Vehicle update must be done before the goods move with the new vehicle</li>
                      <li>Ensure the new vehicle number is correct</li>
                      <li>This action will be logged in the E-way Bill history</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="il-modal-footer">
              <button 
                className="il-btn il-btn-secondary"
                onClick={handleCloseUpdateVehicleModal}
                disabled={loading}
              >
                Cancel
              </button>
              <button 
                className="il-btn il-btn-primary"
                onClick={handleSubmitVehicleUpdate}
                disabled={
                  loading ||
                  !vehicleUpdateForm.newVehicleNumber ||
                  !vehicleUpdateForm.currentLocation ||
                  !vehicleUpdateForm.reasonForChange
                }
              >
                Update Vehicle
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

